import { useEffect, useState } from "react";

/**
 * React hook that subscribes to a CSS media query and returns whether it currently matches.
 * Falls back to `false` during server-side rendering.
 *
 * @param {string} query CSS media query string (e.g. "(max-width: 768px)").
 * @returns {boolean} Whether the media query currently matches.
 */
export function useMediaQuery(query) {
    const getMatches = () => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
            return false;
        }

        return window.matchMedia(query).matches;
    };

    const [matches, setMatches] = useState(getMatches);

    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
            return undefined;
        }

        const mediaQueryList = window.matchMedia(query);

        const handleChange = (event) => setMatches(event.matches);

        // Immediately synchronise with the current value in case `query` changed.
        handleChange(mediaQueryList);

        if (typeof mediaQueryList.addEventListener === "function") {
            mediaQueryList.addEventListener("change", handleChange);
            return () => mediaQueryList.removeEventListener("change", handleChange);
        }

        mediaQueryList.addListener(handleChange);
        return () => mediaQueryList.removeListener(handleChange);
    }, [query]);

    return matches;
}
